import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminApi, getToken, clearToken, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Copy, Trash2 } from "lucide-react";

const STATUSES = ["NEW", "REVIEWED", "USED IN BOOK", "USED IN VIDEO"];

export default function AdminSubmission() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    load();
  }, [sid]);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.get(`/admin/submissions/${sid}`);
      setSub(res.data);
    } catch (e) {
      if (e.response?.status === 401) { clearToken(); nav("/admin/login"); return; }
      toast.error("Not found.");
    } finally {
      setLoading(false);
    }
  }

  async function setStatus(status) {
    try {
      await adminApi.put(`/admin/submissions/${sid}/status`, { status });
      setSub((s) => ({ ...s, status }));
      toast.success(`Marked ${status}.`);
    } catch (e) { toast.error("Couldn't update."); }
  }

  function fileUrl(path) {
    return `${API}/admin/file?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(getToken())}`;
  }

  function downloadZip() {
    window.open(`${API}/admin/submissions/${sid}/download?auth=${encodeURIComponent(getToken())}`, "_blank");
  }

  async function copyMessage() {
    if (!sub?.message) return;
    await navigator.clipboard.writeText(sub.message);
    toast.success("Message copied.");
  }

  async function del() {
    if (!confirm(`Delete submission from ${sub.name}? This can't be undone.`)) return;
    try {
      await adminApi.delete(`/admin/submissions/${sid}`);
      toast.success("Deleted.");
      nav("/admin");
    } catch (e) { toast.error("Couldn't delete."); }
  }

  if (loading || !sub) return <div className="p-10">Loading…</div>;

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="admin-submission-page">
      <div className="border-b border-ink/15 bg-ink text-cream">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-cream/80 hover:text-cream text-sm" data-testid="back-to-vault">
            <ArrowLeft className="w-4 h-4" /> Back to the vault
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={downloadZip} className="inline-flex items-center gap-2 bg-butter text-ink rounded-full px-4 py-2 text-sm" data-testid="download-zip">
              <Download className="w-4 h-4" /> Download everything
            </button>
            <button onClick={del} className="inline-flex items-center gap-2 border border-cream/30 rounded-full px-4 py-2 text-sm hover:bg-red-500/20" data-testid="delete-submission">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="text-xs tracking-[0.3em] text-ink/60">
          Submitted {new Date(sub.submitted_at).toLocaleString()}
        </div>
        <h1 className="font-display text-5xl md:text-7xl mt-2">{sub.name}</h1>
        {sub.relationship ? <div className="font-hand text-2xl text-ink/70 mt-1">{sub.relationship}</div> : null}

        <div className="mt-6 flex flex-wrap gap-2" data-testid="status-buttons">
          {STATUSES.map((st) => (
            <button
              key={st}
              onClick={() => setStatus(st)}
              className={`text-xs tracking-[0.2em] px-3 py-2 rounded-full border ${sub.status === st ? "bg-ink text-cream border-ink" : "border-ink/20 hover:bg-white"}`}
              data-testid={`status-${st.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {st}
            </button>
          ))}
        </div>

        {sub.three_words?.length ? (
          <div className="mt-8 bg-butter/50 border border-ink/15 rounded-2xl p-5">
            <div className="text-xs tracking-[0.3em] text-ink/60 mb-2">JAMES IN THREE WORDS</div>
            <div className="font-display text-2xl">{sub.three_words.filter(Boolean).join(" · ")}</div>
          </div>
        ) : null}

        <section className="mt-10">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl">Message</h2>
            {sub.message ? (
              <button onClick={copyMessage} className="text-sm inline-flex items-center gap-2 text-ink/70 hover:text-ink" data-testid="copy-message">
                <Copy className="w-4 h-4" /> Copy
              </button>
            ) : null}
          </div>
          <div className="mt-3 bg-white border border-ink/15 rounded-2xl p-6 whitespace-pre-wrap font-body text-lg leading-relaxed" data-testid="message-content">
            {sub.message || <span className="text-ink/40 italic">No written message.</span>}
          </div>
        </section>

        {sub.photos?.length ? (
          <section className="mt-10">
            <h2 className="font-display text-2xl mb-4">Photos ({sub.photos.length})</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="photos-grid">
              {sub.photos.map((p, i) => (
                <a key={p.id} href={fileUrl(p.storage_path)} target="_blank" rel="noreferrer"
                   className={`scrapbook block ${i % 3 === 0 ? "-rotate-1" : i % 3 === 1 ? "rotate-1" : ""}`}
                   data-testid={`photo-${i}`}
                   download>
                  <div className="aspect-square bg-creamdark overflow-hidden">
                    <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover" />
                  </div>
                  <div className="text-xs text-ink/60 mt-1 truncate">{p.original_filename}</div>
                </a>
              ))}
            </div>
          </section>
        ) : null}

        {sub.video ? (
          <section className="mt-10">
            <h2 className="font-display text-2xl mb-4">Video</h2>
            <video src={fileUrl(sub.video.storage_path)} controls className="w-full rounded-2xl bg-black" data-testid="video-player" />
            <a href={fileUrl(sub.video.storage_path)} download
               className="inline-flex items-center gap-2 mt-3 text-sm underline text-ink/70 hover:text-ink"
               data-testid="download-video">
              <Download className="w-4 h-4" /> Download video
            </a>
          </section>
        ) : null}
      </div>
    </div>
  );
}
